import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #17a2b8;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #e0f7fa, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #17a2b8;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ItemBadge = styled.span`
  background: #17a2b8;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  font-size: 12px;
`;

const DeleteButton = styled(Button)`
  padding: 4px 10px !important;
  font-size: 12px !important;
`;

const AddButton = styled(Button)`
  margin-top: 10px;
  font-size: 13px;
`;

interface SentenceOrderingFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const SentenceOrderingForm: React.FC<SentenceOrderingFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      // Support both 'words' (new) and 'items' (old) formats
      // Support both correct_sentence (old) and correct_answers (new) formats
      if (parsed) {
        let correctAnswers = [''];
        if (parsed.correct_answers && Array.isArray(parsed.correct_answers)) {
          correctAnswers = parsed.correct_answers;
        } else if (parsed.correct_sentence) {
          correctAnswers = [parsed.correct_sentence];
        }
        
        return {
          words: parsed.words || parsed.items || [''],
          correct_answers: correctAnswers,
        };
      }
      
      return {
        words: [''],
        correct_answers: [''],
      };
    } catch {
      return {
        words: [''],
        correct_answers: [''],
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());
  // Initialize sentenceInput by joining existing words with ' / '
  const [sentenceInput, setSentenceInput] = useState(() => {
    const parsed = parseContent();
    const words = parsed.words || [];
    return words.filter((w: string) => w.trim()).join(' / ');
  });

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleItemChange = (index: number, value: string) => {
    const newWords = [...(contentData.words || [])];
    newWords[index] = value;
    setContentData({
      ...contentData,
      words: newWords,
    });
  };
  
  const handleCorrectAnswerChange = (index: number, value: string) => {
    const newAnswers = [...(contentData.correct_answers || [''])];
    newAnswers[index] = value;
    setContentData({
      ...contentData,
      correct_answers: newAnswers,
    });
  };

  const handleAddCorrectAnswer = () => {
    setContentData({
      ...contentData,
      correct_answers: [...(contentData.correct_answers || ['']), ''],
    });
  };

  const handleRemoveCorrectAnswer = (index: number) => {
    if ((contentData.correct_answers || ['']).length <= 1) {
      alert('Phải có ít nhất 1 đáp án đúng!');
      return;
    }
    const newAnswers = (contentData.correct_answers || ['']).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      correct_answers: newAnswers,
    });
  };

  const handleAddItem = () => {
    setContentData({
      ...contentData,
      words: [...(contentData.words || []), ''],
    });
  };

  const handleRemoveItem = (index: number) => {
    if ((contentData.words || []).length <= 1) {
      alert('Phải có ít nhất 1 từ!');
      return;
    }
    const newWords = (contentData.words || []).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      words: newWords,
    });
  };

  const handleSentenceInput = (value: string) => {
    setSentenceInput(value);

    // Auto-split by '/' and populate words (do NOT touch correct_sentence)
    if (value.trim()) {
      const words = value
        .split('/')
        .map((w: string) => w.trim())
        .filter((w: string) => w.length > 0);
      
      if (words.length > 0) {
        setContentData((prev) => ({
          ...prev,
          words,
        }));
      }
    }
  };

  return (
    <FormBox>
      <h5>🔄 Nội dung câu hỏi - Sắp xếp câu</h5>
      
      <FormGroup>
        <Label>Đề bài (Nhập các từ, ngăn cách bằng dấu /): *</Label>
        <Input
          type="text"
          placeholder="Ví dụ: Do / you / like / apples?"
          value={sentenceInput}
          onChange={(e) => handleSentenceInput(e.target.value)}
          style={{ marginBottom: '15px' }}
        />
        <small className="form-text text-muted" style={{ marginBottom: '15px', display: 'block' }}>
          💡 Nhập các từ ngăn cách bằng dấu "/" (gạch chéo). Các từ sẽ tự động được thêm vào danh sách bên dưới.
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Các từ cần sắp xếp: *</Label>
        <ItemsList>
          {(contentData.words || []).map((item: string, index: number) => (
            <ItemRow key={index}>
              <ItemBadge>{index + 1}</ItemBadge>
              <Input
                type="text"
                value={item}
                onChange={(e) => handleItemChange(index, e.target.value)}
                placeholder={`Từ thứ ${index + 1}...`}
                style={{ flex: 1 }}
              />
              <DeleteButton
                color="danger"
                size="sm"
                onClick={() => handleRemoveItem(index)}
                disabled={(contentData.words || []).length <= 1}
              >
                ✕
              </DeleteButton>
            </ItemRow>
          ))}
        </ItemsList>
        <AddButton
          color="info"
          size="sm"
          onClick={handleAddItem}
        >
          + Thêm từ
        </AddButton>
        <small className="form-text text-muted" style={{ marginTop: '10px' }}>
          💡 Các từ sẽ được sắp xếp ngẫu nhiên khi hiển thị cho học sinh
        </small>
      </FormGroup>
      
      <FormGroup>
        <Label>Các đáp án đúng: *</Label>
        <ItemsList>
          {(contentData.correct_answers || ['']).map((answer: string, index: number) => (
            <ItemRow key={index}>
              <ItemBadge>{index + 1}</ItemBadge>
              <Input
                type="text"
                value={answer}
                onChange={(e) => handleCorrectAnswerChange(index, e.target.value)}
                placeholder={`Đáp án đúng thứ ${index + 1}...`}
                style={{ flex: 1 }}
              />
              <DeleteButton
                color="danger"
                size="sm"
                onClick={() => handleRemoveCorrectAnswer(index)}
                disabled={(contentData.correct_answers || ['']).length <= 1}
              >
                ✕
              </DeleteButton>
            </ItemRow>
          ))}
        </ItemsList>
        <AddButton
          color="success"
          size="sm"
          onClick={handleAddCorrectAnswer}
        >
          + Thêm đáp án
        </AddButton>
        <small className="form-text text-muted" style={{ marginTop: '10px' }}>
          💡 Nhập các câu hoàn chỉnh đúng mà học sinh có thể sắp xếp (có thể có nhiều cách sắp xếp đúng)
        </small>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#e0f2f1', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '10px'
      }}>
        <strong style={{ fontSize: '13px', color: '#00695c' }}>📋 Xem trước:</strong>
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            marginBottom: '10px'
          }}>
            {(contentData.words || []).filter((item: string) => item.trim()).map((item: string, i: number) => (
              <span key={i} style={{
                background: 'white',
                padding: '4px 10px',
                borderRadius: '4px',
                border: '1px solid #00897b'
              }}>
                {item}
              </span>
            ))}
          </div>
          {(contentData.correct_answers || ['']).filter((a: string) => a.trim()).length > 0 && (
            <div style={{ color: '#00695c', fontWeight: 600 }}>
              ✓ Các đáp án đúng:
              <ul style={{ margin: '5px 0 0 20px', paddingLeft: 0 }}>
                {(contentData.correct_answers || ['']).filter((a: string) => a.trim()).map((ans: string, idx: number) => (
                  <li key={idx}>{ans}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Preview JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default SentenceOrderingForm;
